import React, { useCallback, useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  AlignItems,
  Box,
  Display,
  FontWeight,
  Icon,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'
import { browserHistory } from 'react-router'

import { client } from 'config/routes'
import { CollectionSlug } from 'routes/Menu/domains/collections'
import { onSpaceOrEnter } from 'utils/accessibility'

type MenuExperienceBackButtonProps = {
  pathname?: string
  query: Record<string, unknown>
  shouldGoBackToBrowse: boolean
  variant: 'desktop' | 'mobile'
}

const getBrowseQuery = (query: Record<string, unknown>) => ({
  ...query,
  collection: CollectionSlug.Browse,
})

export const MenuExperienceBackButton = ({
  pathname = '/menu',
  query,
  shouldGoBackToBrowse,
  variant,
}: MenuExperienceBackButtonProps) => {
  const onClick = useCallback(() => {
    if (shouldGoBackToBrowse) {
      datadogRum.addAction('back_to_browse_button_clicked')
      browserHistory.push({
        pathname,
        query: getBrowseQuery(query),
      })

      return
    }

    datadogRum.addAction('back_to_delivery_details_button_clicked')
    browserHistory.push(client.deliveryDetails)
  }, [pathname, query, shouldGoBackToBrowse])

  const onKeyDown = useMemo(() => onSpaceOrEnter(() => onClick()), [onClick])

  return (
    <Box
      alignItems={AlignItems.Center}
      aria-label="Back Button"
      data-testid={`menu-experience-back-button-${variant}`}
      display={Display.Flex}
      justifyContent={JustifyContent.Center}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      style={{ cursor: 'pointer' }}
      tabIndex={0}
    >
      <Icon name="arrow_back" />
      <Text fontWeight={FontWeight.Bold} size={4}>
        Back
      </Text>
    </Box>
  )
}
